/* One picture cut into a grid of smaller ones.
 *
 * Two errands, and they are the same cut:
 *
 *   an Instagram grid -- a picture posted as nine squares that reassemble into
 *   one image on a profile, which only works if the cuts are exact;
 *
 *   a sprite sheet -- a developer handed one PNG of forty frames who needs the
 *   forty frames, which is the same job with different numbers.
 *
 * NOTHING IS UPLOADED. The whole of this is a canvas on the device, the same
 * bargain /svg-to-png and /mp4-to-gif strike, and here it is not even a
 * trade: cutting moves pixels and there is nothing for a CDN to do that the
 * browser cannot. The alternative -- one upload and N `cr_` directives -- was
 * considered and is worse on every axis that matters: a 6x6 grid is 36 round
 * trips and 36 stored objects for a job that costs nothing here.
 *
 *     tileEdges()   where the cuts fall, and the whole correctness story
 *     tileGrid()    the rectangles
 *     tileName()    what each one is called on disk
 *     cutTiles()    the canvas half
 *
 * The first three are pure so tests/tile_split_test.mjs can hold them without
 * a browser, which is where the only subtle rule lives.
 */

/* THE CUTS ARE ROUNDED FROM THE EDGE, NEVER ACCUMULATED FROM A TILE WIDTH.
 *
 * This is the entire correctness argument and it is one line.
 *
 * The obvious version is `const w = Math.floor(total / count)` and then N
 * tiles of w. On 1000 pixels into 3 that is 333, 333, 333 -- and the last
 * column of the picture is GONE, one pixel of it, silently. On 1080 into 7 it
 * loses 3. Nobody notices on a photograph and everybody notices on an
 * Instagram grid, because the seam between two posts is where the eye goes,
 * and on a sprite sheet it is worse than noticing: a frame is short and the
 * animation jitters.
 *
 * Rounding each BOUNDARY instead -- round(i * total / count) -- makes the
 * tiles differ by at most one pixel, guarantees they abut exactly, and
 * guarantees they cover the whole edge, because the last boundary is
 * round(total) = total by construction rather than by arithmetic that happens
 * to come out even.
 */
export function tileEdges(total, count) {
    const out = []
    for (let i = 0; i <= count; i++) {
        out.push(Math.round((i * total) / count))
    }
    return out
}

/* The smallest grid that still has a pixel in every tile.
 *
 * A 40px-wide sprite sheet asked for 60 columns is not a grid, it is 20 empty
 * files -- and an empty canvas is what a browser hands back rather than an
 * error, which is the failure mode this repository already records for iOS.
 * So the count is bounded by the picture rather than by a constant.
 */
export const MAX_TILES_PER_SIDE = 12

export function tilesFit(width, height, cols, rows) {
    return cols >= 1 && rows >= 1
        && cols <= Math.min(MAX_TILES_PER_SIDE, width)
        && rows <= Math.min(MAX_TILES_PER_SIDE, height)
}

/* The rectangles, in READING ORDER.
 *
 * Reading order and not column-major, because that is the order a file
 * manager shows them in once tileName() has padded the numbers, the order the
 * index page lays them out in, and the order somebody posts them in. One
 * order, decided here, so the zip, the index and the names cannot disagree --
 * the argument galleryPage() already makes about numbering pages by position
 * rather than out of a filename.
 */
export function tileGrid({width, height, cols, rows}) {
    if (!tilesFit(width, height, cols, rows)) {
        return []
    }
    const xs = tileEdges(width, cols)
    const ys = tileEdges(height, rows)
    const out = []
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            out.push({
                row: r + 1,
                col: c + 1,
                x: xs[c],
                y: ys[r],
                w: xs[c + 1] - xs[c],
                h: ys[r + 1] - ys[r]
            })
        }
    }
    return out
}

/* ZERO-PADDED, so the folder is in the right order without anybody sorting it.
 *
 * `frame_r1c10` sorts before `frame_r1c2` in every file manager and in every
 * zip listing, which on a sprite sheet means the frames come out shuffled.
 * The padding is the width of the largest number actually used, so a 3x3 grid
 * is not given two-digit names it does not need.
 */
export function tileName(stem, tile, cols, rows, ext) {
    const pad = (n, of) => String(n).padStart(String(of).length, "0")
    const base = (stem || "picture").replace(/\.[^.]+$/, "") || "picture"
    const suffix = rows > 1 && cols > 1
        ? `r${pad(tile.row, rows)}c${pad(tile.col, cols)}`
        // A single row or a single column is a STRIP, and "r1c04" on a strip
        // is two numbers where one of them is always 1. Somebody slicing a
        // sprite strip wants frame_04.
        : pad(rows > 1 ? tile.row : tile.col, Math.max(rows, cols))
    return `${base}_${suffix}.${ext}`
}

/* What comes out, given what went in.
 *
 * A cut does not change the pixels, so re-encoding one as something else is a
 * loss for no reason -- a PNG screenshot cut into tiles must stay PNG or the
 * flat colour it is made of turns to mush. The exception is the formats a
 * canvas cannot write: everything that is not PNG or WebP comes out as JPEG,
 * which is what toBlob falls back to anyway, silently, if it is not told.
 */
export function tileType(sourceType) {
    const t = String(sourceType || "").toLowerCase()
    if (t.includes("png")) {
        return {mime: "image/png", ext: "png"}
    }
    if (t.includes("webp")) {
        return {mime: "image/webp", ext: "webp"}
    }
    return {mime: "image/jpeg", ext: "jpg"}
}

/* The picture that shows the tiles back in their grid.
 *
 * A sibling of galleryPage() in compositions/utils.js and written for the same
 * moment: the file is opened by double-clicking it off a disk, with no server
 * and no network. So it is self-contained -- no stylesheet, no script, bare
 * relative names -- and it exists because THE ONE THING SOMEBODY WANTS TO
 * CHECK about a set of tiles is whether the cut was right, and the only way to
 * see that is to put them back together.
 *
 * A CSS grid with no gap does exactly that: if the arithmetic in tileEdges()
 * were wrong the seams would show here, in the file the visitor already has
 * open.
 */
export function tilesPage(title, cols, entries) {
    const esc = (s) => String(s).replace(/[&<>"]/g,
        (c) => ({"&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;"}[c]))

    const cells = entries.map((e) => `      <img src="${esc(e.name)}" alt="${
        esc(e.name)}" loading="lazy">`).join("\n")

    return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width,initial-scale=1">
    <title>${esc(title)} &mdash; tiles</title>
    <style>
      :root { color-scheme: light; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        padding: 32px 20px 56px;
        background: #f4f4f6;
        color: #1c1c22;
        font: 16px/1.55 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
              Helvetica, Arial, sans-serif;
      }
      main { max-width: 900px; margin: 0 auto; }
      h1 { margin: 0 0 4px; font-size: 24px; line-height: 1.25; word-break: break-word; }
      .count { margin: 0 0 10px; color: #62626e; font-size: 15px; }
      .credit {
        margin: 0 0 30px; padding-bottom: 18px;
        border-bottom: 1px solid #dededf; color: #62626e; font-size: 14px;
      }
      .credit a, footer a { color: #4f39d9; }
      /* NO GAP, deliberately. A gap would hide exactly the fault this page
         exists to reveal: tiles that do not abut. */
      .grid {
        display: grid;
        grid-template-columns: repeat(${cols}, 1fr);
        gap: 0;
        background: #fff;
        border: 1px solid #dededf;
        border-radius: 6px;
        overflow: hidden;
      }
      .grid img { display: block; width: 100%; height: auto; }
      footer { max-width: 900px; margin: 40px auto 0; color: #62626e; font-size: 14px; }
    </style>
  </head>
  <body>
    <main>
      <h1>${esc(title)}</h1>
      <p class="count">${entries.length} tile${entries.length === 1 ? "" : "s"}, ${
        cols} across. Shown back in their grid, with no gaps, so you can see the cut.</p>
      <p class="credit">Made with <a href="https://onlineimageoptimizer.com"
        target="_blank" rel="noopener">Online Image Optimizer</a> &mdash; a free
        tool that cuts pictures into tiles and makes images smaller, with no
        account and no watermark.</p>
      <div class="grid">
${cells}
      </div>
    </main>
  </body>
</html>
`
}

/* The canvas half. Returns [{name, blob, tile}] in reading order.
 *
 * ONE SCRATCH CANVAS FOR THE WHOLE RUN, resized per tile. Allocating one per
 * tile is 144 canvas allocations on a 12x12 grid, which is the same mistake
 * captioned() in lib/videoFrames.js records paying for on a ten-second clip.
 */
export async function cutTiles({img, cols, rows, stem, sourceType,
                                quality = 0.92, doc = document}) {
    const width = img.naturalWidth || img.width
    const height = img.naturalHeight || img.height
    const tiles = tileGrid({width, height, cols, rows})
    if (!tiles.length) {
        return []
    }

    const {mime, ext} = tileType(sourceType)
    const canvas = doc.createElement("canvas")
    const ctx = canvas.getContext("2d")
    const out = []

    for (const tile of tiles) {
        canvas.width = tile.w
        canvas.height = tile.h
        // A canvas is dirty from the previous tile and the next one may be a
        // pixel smaller. Clearing rather than trusting the resize: assigning
        // width DOES clear it in every browser, and relying on a side effect
        // of a property assignment for correctness is how a transparent PNG
        // comes back carrying a stripe of the tile before it.
        ctx.clearRect(0, 0, tile.w, tile.h)
        ctx.drawImage(img, tile.x, tile.y, tile.w, tile.h, 0, 0, tile.w, tile.h)
        const blob = await new Promise((resolve) => {
            canvas.toBlob(resolve, mime, quality)
        })
        if (!blob) {
            return []
        }
        out.push({name: tileName(stem, tile, cols, rows, ext), blob, tile})
    }
    return out
}
